
/**
 * Adherence API Client Wrapper
 * This class provides methods to interact with the JWT-protected adherence API endpoints.
 */

import Axios, { AxiosInstance } from 'axios'

import * as Env from 'core/env'

/**
 * Interface for API error response
 */
interface APIErrorResponse {
  error: string;
  details?: Record<string, any>;
}

/**
 * Interface for adherence response
 */
interface AdherenceResponse {
  adherenceData: Array<AdherenceResponseItem | AdherenceResponseItemWithStreaks>;
  summary?: AdherenceSummary | null;
}

/**
 * Interface for adherence response item
 */
export interface AdherenceResponseItem {
  accessCode: string;
  prescribingNpi: string;
  physicianName: string;
  amblyopicEye: string | null;
  patientId: string;
  firstDayOfTreatment: string | null;
  lastDayOfTreatment: string | null;
  lastTreatmentDate: string | null;
  numberOfDaysInTreatment: number | null;
  minimumWeeklyUsage: number;
  minimumMonthlyUsage: number;
  totalUsage: number;
  pastWeekUsage: number;
  pastMonthUsage: number;
  isActive: boolean;
  pastMonthPrescribedHours: number | null;
  totalPrescribedHours: number | null;
  totalAdherence: number | null;
  pastWeekAdherence: number | null;
  pastMonthAdherence: number | null;
  dailyUsageData?: DailyUsageItem[] | null;
  archivedAt: string | null;
}

/**
 * Interface for adherence response item with streaks
 */
interface AdherenceResponseItemWithStreaks extends AdherenceResponseItem {
  currentStreak: number | null;
  currentDrop: number | null;
}

/**
 * Interface for adherence summary
 */
export interface AdherenceSummary {
  numberOfCodes: number;
  medianTotalAdherence: number | null;
  meanTotalAdherence: number | null;
  medianPastWeekAdherence: number | null;
  meanPastWeekAdherence: number | null;
}

/**
 * Interface for daily usage item
 */
interface DailyUsageItem {
  date: string;
  totalMinutes: number;
}

/**
 * Adherence API Client
 */
export class AdherenceAPI {
  private client: AxiosInstance;

  constructor(token: string) {
    this.client = Axios.create({
      baseURL: Env.ADHERENCE_API_URL,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      timeout: 30000,
      withCredentials: false,
    });
  }

  /**
   * Get adherence data with optional filters
   * @param isActive Whether the adherence data is active
   * @param npis Optional list of NPIs (each must be exactly 10 digits)
   */
async getAdherence(isActive?: boolean, npis?: string[]): Promise<AdherenceResponse> {
  try {
    // Construct query params dynamically
    const params: Record<string, any> = {};

    if (isActive !== undefined && isActive !== null) {
      params.isActive = isActive;
    }

    if (npis && npis.length > 0) {
      params.npis = npis.join(",");
    }

    const response = await this.client.get<AdherenceResponse>('/jwt/adherence', { params });
    return response.data;
  } catch (error) {
    throw this.handleError(error);
  }
}

/**
 * Get adherence data for a specific prescribing NPI
 * @param prescribingNpi The NPI of the prescribing physician (must be exactly 10 digits)
 * @param isActive Whether the adherence data is active
 */
async getAdherenceForNpi(prescribingNpi: string, isActive?: boolean): Promise<AdherenceResponse> {
  try {
    const params: Record<string, any> = {};

    if (isActive !== undefined && isActive !== null) {
      params.isActive = isActive;
    }

    const response = await this.client.get<AdherenceResponse>(
      `/jwt/adherence/npi/${prescribingNpi}`,
      { params }
    );
    return response.data;
  } catch (error) {
    throw this.handleError(error);
  }
}

/**
 * Get adherence data for a specific prescribing NPI and access code
 * @param prescribingNpi The NPI of the prescribing physician (must be exactly 10 digits)
 * @param accessCode The access code for adherence details (max 60 characters)
 * @param isActive Whether the adherence data is active
 * @param dailyDosages Whether to include daily dosages in the output
 */
async getAdherenceWithAccessCode(
  prescribingNpi: string,
  accessCode: string,
  isActive?: boolean,
  dailyDosages: boolean = true
): Promise<AdherenceResponseItem> {
  try {
    const params: Record<string, any> = { dailyDosages };

    if (isActive !== undefined && isActive !== null) {
      params.isActive = isActive;
    }

    const response = await this.client.get<AdherenceResponseItem>(
      `/jwt/adherence/npi/${prescribingNpi}/access-code/${accessCode}`,
      { params }
    );

    return response.data;
  } catch (error) {
    throw this.handleError(error);
  }
}

  /**
   * Fetch API metadata
   */
  async getAPIInfo(): Promise<Record<string, any>> {
    try {
      const response = await this.client.get<Record<string, any>>('/info/');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param error The error object from Axios
   */
  private handleError(error: any): APIErrorResponse {
    if (error.response) {
      return {
        error: (error.response.data && error.response.data.error) ? error.response.data.error : 'API Error',
        details: (error.response.data && error.response.data.details) ? error.response.data.details : {},
      };
    }
    return { error: 'Network Error', details: {} };
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./core/src/api/adherence-client.ts